import {
  AlertCircleIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  PrinterIcon,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/catalyst/button';
import type {
  TreatmentRecordRequestChannelSchema,
  TreatmentRecordRequestSchema,
} from '@/services/api/generated/treatmentRecordRequestsService';
import type {
  AlternateContact,
  AlternateContactCollection,
} from '@/types/treatment-request-alternates';
import { canonicalizeHandleType } from '@/utils/treatment-request-alternates';

import { ResendRequestModal } from '../resend/ResendRequestModal';

interface RequestResendSectionProps {
  request: TreatmentRecordRequestSchema;
  matterId: string;
  providerId: string;
  providerServiceId?: string | null;
  alternates: AlternateContact[];
  alternatesCollection: AlternateContactCollection | null;
  alternatesLoading: boolean;
}

const formatHandleType = (type: string) => {
  const canonical = canonicalizeHandleType(type);
  switch (canonical) {
    case 'FAX':
      return 'Fax Number';
    case 'PHONE':
      return 'Phone Number';
    case 'EMAIL':
      return 'Email Address';
    case 'MAILING_ADDRESS':
      return 'Mailing Address';
    default:
      return canonical;
  }
};

const getHandleIcon = (type: string) => {
  const canonical = canonicalizeHandleType(type);
  switch (canonical) {
    case 'FAX':
      return <PrinterIcon className="h-5 w-5" />;
    case 'PHONE':
      return <PhoneIcon className="h-5 w-5" />;
    case 'EMAIL':
      return <MailIcon className="h-5 w-5" />;
    case 'MAILING_ADDRESS':
    default:
      return <MapPinIcon className="h-5 w-5" />;
  }
};

const getConfidenceBadge = (confidence: number | null | undefined) => {
  if (confidence === null || confidence === undefined) {
    return {
      label: 'Confidence N/A',
      className: 'bg-zinc-100 text-zinc-600',
      percentage: '—',
    };
  }

  const percentage = Math.round(confidence * 100);

  if (confidence >= 0.9) {
    return {
      label: 'High Confidence',
      className: 'bg-emerald-100 text-emerald-700',
      percentage: `${percentage}%`,
    };
  }

  if (confidence >= 0.7) {
    return {
      label: 'Medium Confidence',
      className: 'bg-amber-100 text-amber-700',
      percentage: `${percentage}%`,
    };
  }

  return {
    label: 'Low Confidence',
    className: 'bg-rose-100 text-rose-700',
    percentage: `${percentage}%`,
  };
};

const getAlternateKey = (alternate: AlternateContact) =>
  `${canonicalizeHandleType(alternate.handle_type)}::${alternate.value.trim()}`;

const normalizeContactValue = (value: string, type: string): string => {
  const canonical = canonicalizeHandleType(type);

  switch (canonical) {
    case 'FAX':
    case 'PHONE':
      return value.replace(/[^\d+]/g, '');
    case 'EMAIL':
      return value.trim().toLowerCase();
    case 'MAILING_ADDRESS':
      return value.replace(/\s+/g, ' ').trim().toLowerCase();
    default:
      return value.trim();
  }
};

const getCurrentChannelValue = (
  channel: TreatmentRecordRequestChannelSchema | null | undefined,
  alternateType: string,
): string | null => {
  if (!channel) return null;

  const canonical = canonicalizeHandleType(alternateType);
  const channelType = canonicalizeHandleType(channel.type);

  if (canonical !== channelType) return null;

  switch (canonical) {
    case 'FAX':
      return channel.fax || null;
    case 'PHONE':
      return channel.phone_number || null;
    case 'EMAIL':
      return channel.email || null;
    case 'MAILING_ADDRESS':
      return channel.address || null;
    default:
      return null;
  }
};

const getChannelValue = (
  channel: TreatmentRecordRequestChannelSchema | null | undefined,
): string | undefined => {
  if (!channel) return undefined;
  switch (channel.type) {
    case 'fax':
      return channel.fax ?? undefined;
    case 'email':
      return channel.email ?? undefined;
    case 'mail':
      return channel.address ?? undefined;
    case 'phone':
      return channel.phone_number ?? undefined;
    default:
      return undefined;
  }
};

export const RequestResendSection = ({
  request,
  matterId,
  providerId,
  providerServiceId,
  alternates,
  alternatesCollection,
  alternatesLoading,
}: RequestResendSectionProps) => {
  const taskId = request.task_id;
  const hasTask = Boolean(taskId);

  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  const metadata = request.metadata || {};
  const supersededByRequestId =
    (metadata as Record<string, unknown>)?.superseded_by_request_id ??
    undefined;
  const supersessionReason =
    (metadata as Record<string, unknown>)?.supersession_reason ?? undefined;

  const isSuperseded = Boolean(
    typeof supersededByRequestId === 'string' && supersededByRequestId,
  );

  const handleAlternateClick = useCallback(() => {
    setIsResendModalOpen(true);
  }, []);

  const uniqueAlternates = useMemo(() => {
    return alternates.filter((alternate) => {
      const currentValue = getCurrentChannelValue(
        request.channel,
        alternate.handle_type,
      );

      if (!currentValue) return true;

      const norm1 = normalizeContactValue(currentValue, alternate.handle_type);
      const norm2 = normalizeContactValue(
        alternate.value,
        alternate.handle_type,
      );

      return norm1 !== norm2;
    });
  }, [alternates, request.channel]);

  const alternatesHeader = useMemo(() => {
    if (!alternatesCollection) {
      return null;
    }

    const source =
      alternatesCollection.origin === 'voice_ai_runs'
        ? 'Voice AI call summary'
        : 'Task updates';

    const detectedAt =
      alternatesCollection.detectedAt &&
      new Date(alternatesCollection.detectedAt).toLocaleString();

    return (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-zinc-700">
          Discovered Alternate Contacts
        </h4>
        <span className="text-xs text-zinc-500">
          {source}
          {detectedAt ? ` • ${detectedAt}` : null}
        </span>
      </div>
    );
  }, [alternatesCollection]);

  const supersededCopy = useMemo(() => {
    if (!supersessionReason || typeof supersessionReason !== 'string') {
      return 'A newer request is now tracking delivery for this provider.';
    }

    const reason = supersessionReason.toLowerCase();
    if (reason.includes('alternate')) {
      return 'This request was superseded after we accepted an alternate contact method.';
    }
    if (reason.includes('current') || reason.includes('same')) {
      return 'This request was superseded after resending via the same contact method.';
    }
    return supersessionReason;
  }, [supersessionReason]);

  const shouldShowAlternates =
    !isSuperseded && hasTask && uniqueAlternates.length > 0;

  if (!isSuperseded && !shouldShowAlternates && !alternatesLoading) {
    return null;
  }

  return (
    <div className="space-y-4">
      {isSuperseded && typeof supersededByRequestId === 'string' && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <AlertCircleIcon className="mt-0.5 h-5 w-5 text-amber-600" />
          <div className="space-y-1 text-sm text-amber-800">
            <p className="font-semibold text-amber-900">
              This request has been superseded
            </p>
            <p>{supersededCopy}</p>
            <Link
              to={`/matters/${matterId}/providers/${providerId}/record-requests/${supersededByRequestId}`}
              className="text-sm font-medium text-amber-900 underline"
            >
              View current request →
            </Link>
          </div>
        </div>
      )}

      {shouldShowAlternates && (
        <div className="rounded-lg border border-zinc-200 bg-white p-4">
          <div className="space-y-3">
            {alternatesHeader}
            <div className="space-y-2">
              {uniqueAlternates.map((alternate) => {
                const key = getAlternateKey(alternate);
                const canonicalType = canonicalizeHandleType(
                  alternate.handle_type,
                );
                const supportsAccept =
                  canonicalType === 'FAX' ||
                  canonicalType === 'MAILING_ADDRESS';
                const isDisabled = !supportsAccept;
                const badge = getConfidenceBadge(alternate.confidence);

                return (
                  <div
                    key={key}
                    className="flex flex-col gap-3 rounded-md border border-zinc-200 bg-zinc-50 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-zinc-500">
                        {getHandleIcon(alternate.handle_type)}
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-zinc-900">
                          {alternate.label ||
                            formatHandleType(alternate.handle_type)}
                        </div>
                        <div className="font-mono text-xs text-zinc-600">
                          {alternate.value}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-1 font-medium ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                          {badge.percentage !== '—' && (
                            <span className="text-zinc-500">
                              {badge.percentage}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <Button
                        color="indigo-steel"
                        className="h-9 px-3 text-sm"
                        onClick={handleAlternateClick}
                        disabled={isDisabled}
                      >
                        Accept & Resend
                      </Button>
                      {!supportsAccept && (
                        <span className="text-xs text-zinc-500">
                          Manual resend required for{' '}
                          {formatHandleType(canonicalType)}.
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <ResendRequestModal
        open={isResendModalOpen}
        onClose={() => {
          setIsResendModalOpen(false);
        }}
        matterId={matterId}
        providerId={providerId}
        requestId={request.id}
        providerServiceId={providerServiceId}
        recordType={request.provided_records?.[0]}
        currentScope={request.provided_records?.join(', ')}
        currentChannelType={request.channel?.type}
        currentChannelValue={getChannelValue(request.channel)}
      />
    </div>
  );
};
